import React, { useState, useEffect } from 'react';

// Shared by the dashboard's Embed tab builders: a little of Discord's markdown for the previews, and a hex colour box.

export const isLink = (v) => /^https?:\/\/\S+$/.test((v || '').trim());

const INLINE = [
  [/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/, (m, k, ctx) => <a key={k} href={m[2]} target="_blank" rel="noreferrer" className="text-[#00a8fc] hover:underline">{inline(m[1], ctx)}</a>],
  [/\*\*([\s\S]+?)\*\*/, (m, k, ctx) => <strong key={k} className="font-bold">{inline(m[1], ctx)}</strong>],
  [/__([\s\S]+?)__/, (m, k, ctx) => <u key={k}>{inline(m[1], ctx)}</u>],
  [/~~([\s\S]+?)~~/, (m, k, ctx) => <s key={k}>{inline(m[1], ctx)}</s>],
  [/\|\|([\s\S]+?)\|\|/, (m, k, ctx) => <span key={k} className="rounded bg-[#1e1f22] px-0.5">{inline(m[1], ctx)}</span>],
  [/\*([^*\n]+?)\*|\b_([^_\n]+?)_\b/, (m, k, ctx) => <em key={k}>{inline(m[1] || m[2], ctx)}</em>],
  [/`([^`\n]+)`/, (m, k) => <code key={k} className="px-1 rounded bg-[#1e1f22] text-[0.85em]">{m[1]}</code>],
  [/<#(\d+)>/, (m, k, ctx) => <span key={k} className="px-0.5 rounded bg-[#5865f2]/30 text-[#c9cdfb]">#{ctx.channels.find(c => c.id === m[1])?.name || 'unknown'}</span>],
  [/<@&(\d+)>/, (m, k, ctx) => <span key={k} className="px-0.5 rounded bg-[#5865f2]/30 text-[#c9cdfb]">@{ctx.roles.find(r => r.id === m[1])?.name || 'unknown-role'}</span>],
  [/@(everyone|here)\b/, (m, k) => <span key={k} className="px-0.5 rounded bg-[#5865f2]/30 text-[#c9cdfb]">@{m[1]}</span>],
];

export function inline(text, ctx, depth = 0) {
  if (!text || depth > 8) return text;
  let best = null;
  for (const [pattern, render] of INLINE) {
    const m = pattern.exec(text);
    if (m && (!best || m.index < best.m.index)) best = { m, render };
  }
  if (!best) return text;
  const { m, render } = best;
  return [text.slice(0, m.index), render(m, `${depth}-${m.index}`, ctx), ...[].concat(inline(text.slice(m.index + m[0].length), ctx, depth + 1))];
}

export const Markdown = ({ text, ctx, className = '' }) => (
  <div className={`whitespace-pre-wrap break-words ${className}`}>
    {String(text || '').split('\n').map((line, i) => {
      const heading = /^(#{1,3}) (.+)/.exec(line);
      if (heading) {
        const size = ['text-[1.5em]', 'text-[1.25em]', 'text-[1.1em]'][heading[1].length - 1];
        return <div key={i} className={`${size} font-bold leading-snug my-1`}>{inline(heading[2], ctx)}</div>;
      }
      if (/^> /.test(line)) return <div key={i} className="border-l-4 border-[#4e5058] pl-2">{inline(line.slice(2), ctx)}</div>;
      if (/^-# /.test(line)) return <div key={i} className="text-[0.8em] text-[#949ba4]">{inline(line.slice(3), ctx)}</div>;
      if (/^[-*] /.test(line)) return <div key={i} className="pl-4 relative"><span className="absolute left-1">•</span>{inline(line.slice(2), ctx)}</div>;
      return <div key={i}>{line ? inline(line, ctx) : ' '}</div>;
    })}
  </div>
);

// "#ff0000", "ff0000", "#f00" or "f00" → "#ff0000"; anything else → null
export const normaliseHex = (text) => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((text || '').trim());
  if (!m) return null;
  const hex = m[1].length === 3 ? m[1].split('').map(c => c + c).join('') : m[1];
  return `#${hex.toLowerCase()}`;
};

// A colour picker plus a box to type a hex code into: the colour only changes once what's typed is a real colour
export const HexInput = ({ value, onChange, allowEmpty = false, swatches = [] }) => {
  const [text, setText] = useState(value || '');
  useEffect(() => { setText(value || ''); }, [value]);
  const valid = normaliseHex(text) || (allowEmpty && !text.trim());
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <input type="color" value={value || '#5865f2'} onChange={e => onChange(e.target.value)}
        className="h-7 w-9 rounded bg-transparent border border-zinc-700 cursor-pointer flex-shrink-0" />
      <input value={text} placeholder={allowEmpty ? 'No colour' : '#5865f2'} spellCheck={false}
        onChange={e => {
          setText(e.target.value);
          const hex = normaliseHex(e.target.value);
          if (hex) onChange(hex);
          else if (allowEmpty && !e.target.value.trim()) onChange('');
        }}
        onBlur={() => setText(normaliseHex(text) || (allowEmpty && !text.trim() ? '' : value || ''))}
        className={`w-24 rounded-md bg-zinc-950 border px-2 py-1 text-xs font-mono text-white focus:outline-none ${valid ? 'border-zinc-700 focus:border-zinc-500' : 'border-red-500'}`} />
      {swatches.map(c => (
        <button key={c} type="button" onClick={() => onChange(c)} title={c}
          className={`h-5 w-5 rounded-full border flex-shrink-0 ${(value || '').toLowerCase() === c ? 'border-white' : 'border-zinc-700'}`} style={{ background: c }} />
      ))}
      {allowEmpty && value && <button type="button" onClick={() => onChange('')} className="text-[11px] text-zinc-500 hover:text-white">No colour</button>}
    </div>
  );
};

export const SWATCHES = ['#5865f2', '#fbe510', '#ff4500', '#9146ff', '#ff0000', '#23a55a', '#04aff7', '#ff73fa', '#ffffff', '#1e1f22'];
